import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static values = {
    percent: { type: Number, default: 100 },
    threshold: { type: Number, default: 0 },
  };
  static targets = ['bar'];

  connect() {
    if (this.skipsAnimation) {
      this.barTarget.style.width = `${this.percentValue}%`;
      return;
    }

    this.empty();

    this.observer = new IntersectionObserver(
      ([entry]) => {
        if (!this.shouldAnimate(entry)) return;

        this.animate();
        this.observer.disconnect();
      },
      // reachableThreshold is recomputed on every callback, so the observer
      // has to be able to notify wherever it lands.
      { threshold: Array.from({ length: 11 }, (_, step) => step / 10) }
    );
    this.observer.observe(this.element);
  }

  get skipsAnimation() {
    return (
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      document.documentElement.hasAttribute('data-turbo-preview')
    );
  }

  shouldAnimate(entry) {
    const rootTop = entry.rootBounds ? entry.rootBounds.top : 0;
    const scrolledPast = entry.boundingClientRect.top < rootTop;

    if (scrolledPast) return true;

    return entry.isIntersecting && entry.intersectionRatio >= this.reachableThreshold;
  }

  disconnect() {
    this.observer?.disconnect();
  }

  // An element taller than the viewport can never reach a high ratio.
  get reachableThreshold() {
    const height = this.element.offsetHeight;
    if (!height) return 0;

    return Math.min(this.thresholdValue, (window.innerHeight / height) * 0.9);
  }

  // The bar is server-rendered at its final width, so emptying it would itself
  // be animated: a bar already on screen when the controller connects would ease
  // down for the few frames before animate() reverses it, and all that shows is
  // a small wobble instead of a fill.
  empty() {
    const bar = this.barTarget;
    const transition = bar.style.transition;

    bar.style.transition = 'none';
    bar.style.width = '0%';
    bar.getBoundingClientRect();
    bar.style.transition = transition;
  }

  // One painted frame at 0% before the sweep starts, otherwise part of it is
  // spent before the browser has put anything on screen.
  animate() {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!this.element.isConnected) return;

        this.barTarget.style.width = `${this.percentValue}%`;
      });
    });
  }
}
